import { consentOverview, isConsentAnswer } from './consent.js'
import { type Answer, type Check, type Verwijzingen, fitsFileKind, isFileKind } from '@eformulieren/model'
import {
  inCount,
  characterCount,
  wordCount,
  totalWordCount,
  documentsOf,
  extensionOf,
  isEmpty,
  textsOf,
  textOf,
  singleValueOf,
  rowsOf,
  rowIsEmpty,
} from './state.js'
import { patternMatches } from './pattern.js'

/**
 * De regelsoorten uit B11 en `geheelGetal`, uitgerekend (ontwerp 5.3).
 *
 * Een pure functie per soort: geen I/O, geen netwerk, geen tijd. Dezelfde invoer
 * geeft altijd dezelfde uitkomst (R-01e), en dat is wat het isomorfiebewijs van
 * M1 mogelijk maakt (5.9). Wat de uitkomst met de indienpoort doet, staat niet
 * hier maar in de evaluator: hard is een poort, waarschuwing is advies (R-23).
 *
 * **Een leeg antwoord voldoet, op één soort na.** Wie niets invult overtreedt geen
 * woordlimiet en geen getalsbereik. Dat een veld gevuld moet zijn is een eigen
 * regelsoort (R-26), en die staat er los naast; hem hier nog eens inbakken zou
 * elke controle stilzwijgend verplicht maken. De uitzondering is `akkoordJa`: die
 * soort bestaat om een poort te zijn (R-19, R-89), dus een niet-gezet akkoord
 * faalt.
 */

/** Een controle op precies één veld (besluit bij R-29). */
export type FieldCheck = Extract<Check, { doelVeld: string }>

/** Een controle op een hele sectie; in B11 is dat alleen `korteSectie`. */
export type SectionCheck = Extract<Check, { doelSectie: string }>

/** Of deze controle een sectie als onderwerp heeft in plaats van een veld. */
export function onSection(check: Check): check is SectionCheck {
  return 'doelSectie' in check
}

/**
 * Een megabyte is een miljoen bytes.
 *
 * Ontwerp 9.2 schrijft de schrijfgrens van de pod als "1 MB per losse wijziging
 * (een miljoen bytes)". Dezelfde rekeneenheid hier, want een invuller die 20 MB
 * leest en 20 971 520 bytes krijgt begrijpt de melding niet.
 */
const MB_IN_BYTES = 1_000_000

/** Rekent één controle op een veld uit. True betekent: deze regel is gehaald. */
/** Wat de opslag van een aangeleverd bestand weet: de soort uit de controle op de inhoud, en de grootte. */
export interface FileFacts {
  readonly soort: string
  readonly grootteBytes: number
}

/**
 * Wat een controle naast het antwoord nodig kan hebben (R-104, U25).
 *
 * `bestanden`: per hash de feiten uit de opslag, voor de bestanden die voor het dossier van deze instantie
 * zijn aangeleverd. Met deze kaart toetst `bestandseisen` ook de soort, en faalt een verwijzing die de
 * opslag niet kent; de verwijzing in de stand houdt zo haar vorm, want een oude pod leest haar strikt
 * (D-36). Zonder de kaart, zoals in de preview van de configurator, blijft de toets die op naam en
 * grootte. De pod geeft de browser en de indienpoort dezelfde kaart, zodat beide hetzelfde uitrekenen.
 */
export interface CheckContext {
  readonly bestanden?: ReadonlyMap<string, FileFacts>
  /**
   * De opgeloste verwijzingen, voor een voorwaarde op een verwijzing (eisenboek 12.31, keuze 2). De pod
   * rekent ze uit de instantie (`referenceValues`), de browser krijgt ze bij de projectie; zonder is elke
   * verwijzing leeg (keuze 4). Alleen de zichtbaarheid leest ze, geen controle.
   */
  readonly verwijzingen?: Verwijzingen
}

export function fieldCheckPasses(check: FieldCheck, response: Answer | undefined, context: CheckContext = {}): boolean {
  switch (check.soort) {
    case 'verplicht':
      // Bij een akkoord per persoon is verplicht: minstens één akkoord dat nog geldt (eisenboek 12.56).
      // `waarde` is daar een waarheidswaarde, en `false` is niet leeg; daarom deze tak.
      if (isConsentAnswer(response)) return consentOverview(response).geldig > 0
      return !isEmpty(response)

    case 'akkoordJa':
      // De enige soort waarbij leeg faalt: hij bestaat om een poort te zijn.
      return singleValueOf(response) === true

    case 'maximumTekens': {
      const text = textOf(response)
      return text === undefined || characterCount(text) <= check.maximum
    }

    case 'maximumWoorden': {
      const text = textOf(response)
      return text === undefined || wordCount(text) <= check.maximum
    }

    case 'minimumWoorden': {
      // Leeg voldoet: dat een veld gevuld moet zijn is `verplicht` (R-26).
      if (isEmpty(response)) return true
      const text = textOf(response)
      return text !== undefined && wordCount(text) >= check.minimum
    }

    case 'getalsbereik': {
      if (isEmpty(response)) return true
      const value = singleValueOf(response)
      // Een waarde die geen getal is valt buiten elk bereik. Stil doorlaten zou
      // betekenen dat een fout van de client de controle uitzet.
      if (typeof value !== 'number' || !Number.isFinite(value)) return false
      if (check.minimum !== undefined && value < check.minimum) return false
      if (check.maximum !== undefined && value > check.maximum) return false
      return true
    }

    case 'geheelGetal': {
      if (isEmpty(response)) return true
      const value = singleValueOf(response)
      // Net als bij het getalsbereik: een waarde die geen getal is, is ook geen geheel getal.
      return typeof value === 'number' && Number.isInteger(value)
    }

    case 'datumbereik': {
      if (isEmpty(response)) return true
      const date = datePart(singleValueOf(response))
      if (date === undefined) return false
      if (check.vroegste !== undefined && date < check.vroegste) return false
      if (check.laatste !== undefined && date > check.laatste) return false
      return true
    }

    case 'identifierFormaat': {
      if (isEmpty(response)) return true
      const text = textOf(response)
      return text !== undefined && FORMAT[check.formaat](text.trim())
    }

    case 'minimumAantal': {
      // Een veld zonder antwoord heeft er nul, niet onbekend: een herhaalgroep
      // waaraan niemand een rij toevoegde heeft geen rijen. En een lege rij telt niet
      // mee (besluit van 30 oktober 2026): twee lege rijen maakten "minstens één
      // werkpakket" gehaald. Voor het maximum telt hij wel, zie hieronder.
      const rows = rowsOf(response)
      if (rows !== undefined) return rows.filter((row) => !rowIsEmpty(row)).length >= check.minimum
      return (inCount(response) ?? 0) >= check.minimum
    }

    case 'maximumAantal':
      return (inCount(response) ?? 0) <= check.maximum

    case 'bestandseisen': {
      const documents = documentsOf(response) ?? []
      if (documents.length > check.maximumAantal) return false
      const allowed = new Set(check.typen.map((type) => type.toLowerCase()))
      const boundary = check.maximumGrootteMb * MB_IN_BYTES
      return documents.every((document) => {
        const extension = extensionOf(document.bestandsnaam)
        // Een bestand zonder extensie is geen bestand van een toegestaan type.
        if (extension === undefined || !allowed.has(extension)) return false
        if (document.grootteBytes > boundary) return false
        if (context.bestanden === undefined) return true
        // De inhoud is wat de opslag vaststelde, niet wat de naam zegt: een docx die `plan.pdf` heet,
        // is geen pdf. Een soort van buiten de lijst telt niet, wat de definitie ook toestaat.
        const facts = context.bestanden.get(document.hash)
        if (facts === undefined || facts.grootteBytes !== document.grootteBytes) return false
        return isFileKind(facts.soort) && fitsFileKind(document.bestandsnaam, facts.soort)
      })
    }

    case 'regexVorm': {
      if (isEmpty(response)) return true
      const text = textOf(response)
      if (text === undefined) return false
      // Geen trim, anders dan bij identifierFormaat: bij een eigen patroon is het patroon de baas over wat
      // wel en niet meedoet. Het patroon geldt voor de hele waarde, en de matcher rekent lineair in de lengte
      // van de invoer, dus geen patroon houdt de evaluator op (ontwerp 5.3, eisenboek 12.43). Een tekst boven
      // de 10.000 tekens of een patroon buiten de taal heeft geen uitkomst, en dan is de controle niet gehaald.
      return patternMatches(check.expressie, text)
    }

    case 'verbodenTermen': {
      const texts = textsOf(response).map((text) => text.toLowerCase())
      // Zonder woordgrens: in het Nederlands zit een afgeraden term vaak in een
      // samenstelling ("vertrouwelijkheid"), en dit is een waarschuwing, dus te
      // veel opmerken is goedkoper dan te weinig.
      return !check.termen.some((term) => {
        const searched = term.toLowerCase()
        return texts.some((text) => text.includes(searched))
      })
    }

    default:
      return unknownKind(check)
  }
}

/**
 * Rekent een controle op een sectie uit, over de teksten van die sectie.
 *
 * De teksten komen van de evaluator, die ze uit de zichtbare velden haalt: een
 * verborgen veld telt niet mee (R-34), ook niet in een woordendrempel.
 */
export function sectionCheckPasses(check: SectionCheck, texts: readonly string[]): boolean {
  // Geen vangnettak: `korteSectie` is nu de enige sectiecontrole in B11, en zolang
  // dat zo is dekt deze switch het type volledig. Komt er een tweede, dan haalt de
  // typecheck deze functie onderuit op een ontbrekend return, en dat is precies de
  // melding die je dan wil.
  switch (check.soort) {
    case 'korteSectie':
      return totalWordCount(texts) >= check.drempelWoorden
  }
}

/** De datum uit een waarde, of undefined als er geen datum in staat. */
function datePart(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  // Een `datetime` levert een tijdstip; het datumbereik gaat over de dag ervan.
  const found = /^(\d{4}-\d{2}-\d{2})/.exec(value)
  return found?.[1]
}

/**
 * De formaatcontroles per identifiersoort (R-27).
 *
 * Vorm en niet bestaan: of een DOI oplosbaar is loopt via output registreren
 * (R-16, ontwerp 5.4), niet via een regel. De ORCID-controle toetst de vorm en
 * niet het controlecijfer; dat cijfer hoort bij de ORCID-koppeling van R-17 (M7),
 * waar de identifier ook echt wordt opgehaald, en een melding over een
 * controlecijfer helpt een invuller niet verder dan een melding over de vorm.
 */
const FORMAT: Record<Extract<Check, { soort: 'identifierFormaat' }>['formaat'], (text: string) => boolean> = {
  doi: (text) => /^10\.\d{4,9}\/\S+$/.test(text),
  orcid: (text) => /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/.test(text),
  url: (text) => {
    try {
      const url = new URL(text)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  },
  // Vorm en niet bezorgbaarheid: één apenstaartje, geen witruimte, en een punt in
  // het domein. Strenger zou geldige adressen gaan weigeren.
  email: (text) => /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(text),
  telefoon: (text) => {
    if (!/^\+?[\d\s()-]+$/.test(text)) return false
    const digitCount = text.replace(/\D/g, '').length
    // Zeven tot vijftien cijfers: de bovengrens is die van E.164.
    return digitCount >= 7 && digitCount <= 15
  },
}

function unknownKind(check: never): never {
  throw new Error(`geen controle voor regelsoort ${JSON.stringify(check)}`)
}
